<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { getSizeLimitForFile } from '../composables/useMediaValidation';

const props = defineProps({
  modelValue: {
    type: File,
    default: null,
  },
  accept: {
    type: String,
    default: 'image/jpeg,image/png,video/mp4,video/3gpp,application/pdf',
  },
  i18nPrefix: {
    type: String,
    default: 'WHATSAPP_TEMPLATES',
  },
});

const emit = defineEmits(['update:modelValue', 'update:format']);

const { t } = useI18n();

const fileInput = ref(null);
const errorMessage = ref('');
const previewUrl = ref(null);

const acceptedMimes = computed(() =>
  props.accept
    .split(',')
    .map(m => m.trim())
    .filter(Boolean)
);

const detectFormat = file => {
  if (!file) return null;
  const type = file.type || '';
  if (type.startsWith('image/')) return 'IMAGE';
  if (type.startsWith('video/')) return 'VIDEO';
  if (type === 'application/pdf') return 'DOCUMENT';
  return null;
};

const revokePreview = () => {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = null;
  }
};

const setPreview = file => {
  revokePreview();
  if (file) {
    previewUrl.value = URL.createObjectURL(file);
  }
};

watch(
  () => props.modelValue,
  newFile => {
    if (newFile !== null && newFile !== undefined) {
      setPreview(newFile);
    } else {
      revokePreview();
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => revokePreview());

const validateFile = file => {
  if (!acceptedMimes.value.includes(file.type)) {
    errorMessage.value = t(
      `${props.i18nPrefix}.CREATE.MEDIA.INVALID_FILE_TYPE`
    );
    return false;
  }
  const limitMB = getSizeLimitForFile(file);
  if (!checkFileSizeLimit(file, limitMB)) {
    errorMessage.value = t(`${props.i18nPrefix}.CREATE.MEDIA.FILE_SIZE_ERROR`, {
      max: limitMB,
    });
    return false;
  }
  errorMessage.value = '';
  return true;
};

const handleFileChange = event => {
  const file = event.target.files?.[0];
  if (!file) return;

  if (!validateFile(file)) {
    fileInput.value.value = null;
    return;
  }

  emit('update:modelValue', file);
  emit('update:format', detectFormat(file));
};

const handleChooseClick = () => {
  fileInput.value?.click();
};

const handleClear = () => {
  emit('update:modelValue', null);
  emit('update:format', null);
  errorMessage.value = '';
  if (fileInput.value) {
    fileInput.value.value = null;
  }
};

const fileSizeMB = computed(() => {
  if (!props.modelValue) return null;
  return (props.modelValue.size / (1024 * 1024)).toFixed(2);
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <input
      ref="fileInput"
      type="file"
      class="hidden"
      :accept="accept"
      @change="handleFileChange"
    />

    <div v-if="!modelValue" class="flex flex-col gap-2">
      <Button
        type="button"
        variant="faded"
        color="slate"
        icon="i-lucide-upload"
        icon-position="left"
        :label="t(`${i18nPrefix}.CREATE.MEDIA.CHOOSE_FILE`)"
        class="w-fit bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        @click="handleChooseClick"
      />
      <span class="text-xs text-n-slate-11">
        {{ t(`${i18nPrefix}.CREATE.MEDIA.NO_FILE_SELECTED`) }}
      </span>
    </div>

    <div
      v-else
      class="flex items-center justify-between gap-2 p-3 rounded-lg bg-n-alpha-2 border border-n-weak"
    >
      <div class="flex items-center gap-2 min-w-0">
        <i class="i-lucide-file text-base text-n-brand flex-shrink-0" />
        <div class="flex flex-col min-w-0">
          <span class="text-sm font-medium text-n-slate-12 truncate">
            {{ modelValue.name }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{
              t(`${i18nPrefix}.CREATE.MEDIA.FILE_SIZE_VALUE`, {
                size: fileSizeMB,
              })
            }}
          </span>
        </div>
      </div>
      <div class="flex items-center gap-1 flex-shrink-0">
        <Button
          type="button"
          variant="faded"
          color="slate"
          xs
          icon="i-lucide-refresh-cw"
          :label="t(`${i18nPrefix}.CREATE.MEDIA.CHANGE_FILE`)"
          @click="handleChooseClick"
        />
        <Button
          type="button"
          variant="faded"
          color="ruby"
          xs
          icon="i-lucide-trash-2"
          @click="handleClear"
        />
      </div>
    </div>

    <span v-if="errorMessage" class="text-xs text-n-ruby-text">
      {{ errorMessage }}
    </span>
  </div>
</template>
